/**
 * Stripe Checkout Monitor Hook
 *
 * Detects when Stripe payment is complete via Chrome extension.
 * Similar to usePandaDocMonitor but for Stripe checkout.
 *
 * CHROME EXTENSION REQUIRED:
 * - Extension monitors Stripe checkout tab DOM for success
 * - Detects success without CORS restrictions
 * - Auto-closes checkout tab when success detected
 * - Identical to desktop app behavior
 *
 * This hook ONLY detects success - opening Stripe is handled by EmbeddedPricingTable.
 */

import { useEffect, useRef, useState } from 'react';
import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('stripe-checkout-monitor');

export type StripeCheckoutMonitorConfig = {
  /**
   * Callback when payment success is detected
   */
  onSuccess?: (sessionId?: string) => void;

  /**
   * Enable debug logging
   */
  debug?: boolean;
};

export type StripeCheckoutMonitor = {
  /**
   * Check if Chrome extension is available
   */
  hasExtension: boolean;
};

/**
 * Detect if Chrome extension is available
 */
function detectExtension(): boolean {
  // Check if chrome.runtime is available
  if (typeof chrome !== 'undefined' && chrome.runtime?.id) {
    return true;
  }
  return false;
}

/**
 * Hook to monitor Stripe checkout with extension
 */
export const useStripeCheckoutMonitor = (
  config: StripeCheckoutMonitorConfig = {}
): StripeCheckoutMonitor => {
  const { onSuccess, debug = false } = config;

  const [hasExtension] = useState(detectExtension());
  const successCallbackRef = useRef(onSuccess);

  // Update refs when callbacks change
  useEffect(() => {
    successCallbackRef.current = onSuccess;
  }, [onSuccess]);

  // Log extension availability on mount
  useEffect(() => {
    if (debug) {
      if (hasExtension) {
        logger.info(
          '🎯 [STRIPE-MONITOR] Extension detected - payment will be monitored automatically'
        );
      } else {
        logger.warn(
          '⚠️ [STRIPE-MONITOR] No extension detected - payment completion must be detected via other means'
        );
      }
    }
  }, [hasExtension, debug]);

  // Listen for success messages from extension
  useEffect(() => {
    if (!hasExtension) return undefined;

    const handleMessage = (event: MessageEvent) => {
      // Only accept messages from our extension
      if (event.source !== window) return;

      const message = event.data;
      if (message.type === 'STRIPE_CHECKOUT_SUCCESS') {
        if (debug) {
          logger.info('✅ [STRIPE-MONITOR] Success notification from extension:', {
            sessionId: message.sessionId,
          });
        }

        successCallbackRef.current?.(message.sessionId);
      }
    };

    window.addEventListener('message', handleMessage);

    // Also listen for chrome.runtime messages (if in extension context)
    let cleanupRuntimeListener: (() => void) | undefined;

    if (chrome.runtime?.onMessage) {
      const runtimeListener = (
        message: any,
        _sender: chrome.runtime.MessageSender,
        _sendResponse: (response?: any) => void
      ) => {
        if (message.type === 'STRIPE_CHECKOUT_SUCCESS') {
          if (debug) {
            logger.info('✅ [STRIPE-MONITOR] Success from extension runtime:', {
              sessionId: message.sessionId,
            });
          }

          successCallbackRef.current?.(message.sessionId);
        }
      };

      chrome.runtime.onMessage.addListener(runtimeListener);
      cleanupRuntimeListener = () => {
        chrome.runtime.onMessage.removeListener(runtimeListener);
      };
    }

    return () => {
      window.removeEventListener('message', handleMessage);
      cleanupRuntimeListener?.();
    };
  }, [hasExtension, debug]);

  return {
    hasExtension,
  };
};
